import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, PaperPlaneRight, Paperclip, Plus, ShieldCheck, SquaresFour, X } from "@phosphor-icons/react";
import type { components } from "@farmacenter/api";
import { useApi } from "../../api/ApiProvider";
import { useOrg } from "../OrgContext";
import { useNombres } from "../ui/useNombres";
import { SelectorProducto, type ProductoBuscado } from "../ui/SelectorProducto";
import { SelectorTercero } from "../ui/SelectorTercero";
import { useAsistenteApi, type TurnoChat, type AccionPendiente, type FacturaExtraida } from "./asistenteApi";
import {
  esquemaDe,
  esquemaTieneLineas,
  faltantes,
  hayTerminosParciales,
  lineaVacia,
  lineaCompleta,
  lineasAParams,
  terminosParciales,
  valoresAParams,
  valoresIniciales,
  type CampoForm,
  type LineaOrden,
  type OpcionCampo,
} from "./canvasSchema";
import { registrarActividad } from "./novaActividad";
import { useOpcionesRemotas } from "./useOpcionesRemotas";
import novaRobot from "./nova-robot.jpg";

type Presentacion = components["schemas"]["ProductPresentationRepresentation"];

/**
 * Hand-off: herramientas que, en vez de abrir un formulario en el lienzo, LLEVAN al usuario a una
 * pantalla dedicada (lo que no cabe en el chat, como la factura de compra). El backend las propone
 * como acción pendiente; aquí se mapean a su ruta y se navega con lo que Nova reunió como prellenado.
 */
const HANDOFF: Record<string, string> = {
  ir_a_factura_compra: "/compras/factura-nueva",
};

/**
 * Nova — el copiloto de IA del ERP: un lanzador flotante que abre un chat. Pregunta en lenguaje
 * natural sobre los datos del sistema (existencias, ventas, compras, terceros) y responde
 * consultando los endpoints con TU permiso (nunca hace más de lo que tu cuenta puede). Aparece
 * sólo si el backend lo tiene habilitado (hay clave configurada en Organización).
 */

interface Mensaje {
  /** Id estable del mensaje (para animar el streaming UNA sola vez, aún tras remontar). */
  id: number;
  role: "user" | "assistant";
  text: string;
  toolsUsed?: string[];
  /** true en el último mensaje recién llegado de Nova, para revelarlo con efecto de escritura. */
  animar?: boolean;
}

let contadorMensaje = 0;
function nuevoId(): number {
  return ++contadorMensaje;
}
/** Ids cuyo efecto de escritura ya se reprodujo: no se repite al cambiar de pestaña ni remontar. */
const yaAnimados = new Set<number>();

const CLAVE_CHAT = "nova.chat.v1";

/** Carga la conversación guardada (si la hay) y marca todo como ya-animado (nada de re-escribir). */
function cargarConversacion(): Mensaje[] {
  try {
    const crudo = localStorage.getItem(CLAVE_CHAT);
    if (!crudo) return [];
    const datos = JSON.parse(crudo) as Mensaje[];
    if (!Array.isArray(datos)) return [];
    for (const m of datos) {
      if (typeof m.id === "number") {
        yaAnimados.add(m.id);
        contadorMensaje = Math.max(contadorMensaje, m.id);
      }
    }
    return datos;
  } catch {
    return [];
  }
}

function guardarConversacion(mensajes: Mensaje[]) {
  try {
    localStorage.setItem(CLAVE_CHAT, JSON.stringify(mensajes.slice(-40)));
  } catch {
    // sin persistencia si localStorage falla (ventana privada, cuota): no es crítico.
  }
}

const EJEMPLOS = [
  "¿Cuánto acetaminofén tengo en existencia?",
  "Muéstrame las ventas recientes",
  "¿Qué facturas de compra hay pendientes?",
];

/**
 * Identidad de Nova: NARANJA (la marca) para Nova y AZUL (la marca) para el usuario, para que
 * combine con el resto del ERP. `GRAD` es el degradado naranja de los momentos hero (cabeceras,
 * lanzador); `NOVA_SOLID` el naranja sólido de sus acentos (botón enviar); `USER_AZUL` el azul de
 * las burbujas del usuario. Así no se funde siempre el mismo degradado: Nova = naranja, tú = azul.
 */
const GRAD = "linear-gradient(135deg, #fb923c 0%, #f97316 55%, #ea580c 115%)";
const NOVA_SOLID = "#ea580c";
const USER_AZUL = "#2563eb";

/** Id del hueco donde el Panel aloja el chat de Nova a pantalla completa (portal). */
export const NOVA_PANEL_HOST = "nova-panel-host";

export function Asistente() {
  const api = useAsistenteApi();
  const { tenantId, legalEntityId, branchId } = useOrg();

  const estadoQ = useQuery({
    queryKey: ["asistente-estado"],
    queryFn: () => api.estado(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Mensaje[]>(() => cargarConversacion());
  const [entrada, setEntrada] = useState("");
  const [enviando, setEnviando] = useState(false);
  const finRef = useRef<HTMLDivElement | null>(null);
  // Adjuntos: subir una factura (imagen/PDF) y que Nova la lea por OCR y la resuma en el chat.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [leyendoAdjunto, setLeyendoAdjunto] = useState(false);

  // En el Panel (ruta "/") Nova se dibuja a pantalla completa dentro de la página vía portal;
  // en el resto, es la burbuja flotante. Como es la MISMA instancia (montada en App), el estado
  // (conversación, canvas) no se pierde al navegar: se colapsa a la burbuja sin perder progreso.
  const location = useLocation();
  const navigate = useNavigate();
  const enPanel = location.pathname === "/";
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHost(enPanel ? document.getElementById(NOVA_PANEL_HOST) : null);
  }, [enPanel]);

  // Canvas: cuando Nova propone una escritura, se abre un lienzo al lado del chat con un
  // formulario guiado (desplegables, chips, campos tipados). Nada se ejecuta hasta que confirmes.
  const [accion, setAccion] = useState<AccionPendiente | null>(null);
  const [valores, setValores] = useState<Record<string, string | string[]>>({});
  // Las líneas de una orden de compra son objetos (producto+presentación+cantidad), no encajan en
  // 'valores' (texto/arreglo de texto): viven aquí y se fusionan al confirmar.
  const [lineas, setLineas] = useState<LineaOrden[]>([]);
  // Clave de idempotencia por (acción + params): un reintento con el MISMO contenido reusa la clave
  // (el ERP deduplica y no crea un duplicado); editar los datos genera una clave nueva (otra acción).
  // Se vacía al cerrar el lienzo, así una acción nueva idéntica más tarde sí es otra escritura.
  const clavesIdem = useRef<Map<string, string>>(new Map());
  const [ejecutando, setEjecutando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  // Errores por campo devueltos por el ERP (clave del campo → motivo): se pintan junto al campo
  // que falla. Se limpian al editar ese campo, al reintentar y al cerrar el lienzo.
  const [erroresCampo, setErroresCampo] = useState<Record<string, string>>({});
  // Aprobación: al pulsar "Crear" en el formulario se abre un diálogo de RESUMEN Y CONFIRMACIÓN;
  // ahí se crea de verdad (o se vuelve al formulario). Sin auto-aprobar: siempre confirma la persona.
  const [aprobacion, setAprobacion] = useState<{ tool: string; titulo: string; params: Record<string, unknown> } | null>(null);

  const etiquetas = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const t of estadoQ.data?.tools ?? []) {
      mapa.set(t.name, t.label);
    }
    return mapa;
  }, [estadoQ.data]);

  useEffect(() => {
    if (abierto || enPanel) {
      finRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [mensajes, enviando, abierto, enPanel]);

  // Persiste la conversación para que sobreviva a recargar la página (y no se re-anime al volver).
  useEffect(() => {
    guardarConversacion(mensajes);
  }, [mensajes]);

  if (estadoQ.data?.enabled !== true) {
    return null;
  }

  function pushMensaje(m: Omit<Mensaje, "id">) {
    setMensajes((prev) => [...prev, { id: nuevoId(), ...m }]);
  }

  async function enviar(texto: string) {
    const limpio = texto.trim();
    if (!limpio || enviando) {
      return;
    }
    const historial: TurnoChat[] = [
      ...mensajes.map((m): TurnoChat => ({ role: m.role, text: m.text })),
      { role: "user" as const, text: limpio },
    ].slice(-20);
    pushMensaje({ role: "user", text: limpio });
    setEntrada("");
    setEnviando(true);
    registrarActividad("info", "Consulta enviada", limpio.length > 60 ? `${limpio.slice(0, 60)}…` : limpio);
    try {
      const resp = await api.chat(historial, { tenantId, legalEntityId, branchId });
      for (const t of [...new Set(resp.toolsUsed ?? [])]) {
        registrarActividad("info", "Consultó", etiquetas.get(t) ?? t);
      }
      pushMensaje({ role: "assistant", text: resp.reply, toolsUsed: resp.toolsUsed, animar: true });
      if (resp.pendingAction) {
        const pa = resp.pendingAction;
        const rutaHandoff = HANDOFF[pa.tool];
        if (rutaHandoff) {
          // Hand-off: no se abre el lienzo; Nova te lleva a la pantalla dedicada con lo que reunió.
          registrarActividad("aviso", "Te llevo a una pantalla", pa.label);
          navigate(rutaHandoff, { state: { prefillFactura: pa.params } });
        } else {
          registrarActividad("aviso", "Propuso una acción", pa.label);
          abrirCanvas(pa);
        }
      } else {
        registrarActividad("exito", "Respondió");
      }
    } catch (error) {
      const detalle = error instanceof Error && error.message.trim() ? error.message.trim() : null;
      registrarActividad("error", "No pudo responder", detalle ?? undefined);
      pushMensaje({
        role: "assistant",
        text: detalle ?? "No pude responder ahora mismo. Inténtalo de nuevo en un momento.",
      });
    } finally {
      setEnviando(false);
    }
  }

  function alTeclear(evento: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (evento.key === "Enter" && !evento.shiftKey) {
      evento.preventDefault();
      void enviar(entrada);
    }
  }

  async function adjuntar(file: File) {
    if (leyendoAdjunto) {
      return;
    }
    setLeyendoAdjunto(true);
    registrarActividad("info", "Leyendo documento", file.name);
    pushMensaje({ role: "user", text: `📎 ${file.name}` });
    try {
      const base64 = await leerBase64(file);
      const datos = await api.escanearFactura(file.type || "image/jpeg", base64);
      registrarActividad("exito", "Documento leído", file.name);
      pushMensaje({ role: "assistant", text: resumirFactura(datos), animar: true });
    } catch (error) {
      const detalle = error instanceof Error && error.message.trim() ? error.message.trim() : null;
      registrarActividad("error", "No pude leer el documento", file.name);
      pushMensaje({
        role: "assistant",
        text: detalle ?? "No pude leer el documento. Prueba con una imagen o un PDF más nítido.",
      });
    } finally {
      setLeyendoAdjunto(false);
    }
  }

  function abrirCanvas(propuesta: AccionPendiente) {
    setAccion(propuesta);
    setValores(valoresIniciales(propuesta.tool, propuesta.params));
    setLineas([]);
    setErrorAccion(null);
    setErroresCampo({});
  }

  function cerrarCanvas() {
    setAccion(null);
    setValores({});
    setLineas([]);
    setErrorAccion(null);
    setErroresCampo({});
    setAprobacion(null);
    // Cerrado el lienzo, las claves de idempotencia dejan de aplicar: una acción idéntica más
    // tarde debe ser una escritura nueva, no un duplicado deduplicado por una clave vieja.
    clavesIdem.current.clear();
  }

  // Clave estable para una confirmación: misma (acción + params) → misma clave (reintento
  // deduplicado); si cambian los datos, otra clave (otra acción). Ver `clavesIdem`.
  function claveIdempotencia(tool: string, params: Record<string, unknown>): string {
    const firma = `${tool}::${JSON.stringify(params)}`;
    const existente = clavesIdem.current.get(firma);
    if (existente !== undefined) {
      return existente;
    }
    const nueva = crypto.randomUUID();
    clavesIdem.current.set(firma, nueva);
    return nueva;
  }

  // El formulario está completo y se pulsa "Crear": abrimos el resumen de confirmación.
  function solicitarAprobacion() {
    if (!accion || ejecutando) {
      return;
    }
    const base = valoresAParams(valores);
    const params = esquemaTieneLineas(accion.tool)
      ? { ...base, lines: lineasAParams(lineas) }
      : base;
    const titulo = esquemaDe(accion.tool)?.titulo ?? accion.label;
    setAprobacion({ tool: accion.tool, titulo, params });
  }

  async function ejecutar(tool: string, titulo: string, params: Record<string, unknown>) {
    if (ejecutando) {
      return;
    }
    setAprobacion(null);
    setEjecutando(true);
    setErrorAccion(null);
    setErroresCampo({});
    registrarActividad("info", "Ejecutando", titulo);
    try {
      const res = await api.ejecutarAccion(tool, params, { tenantId, legalEntityId, branchId },
        claveIdempotencia(tool, params));
      if (res.ok) {
        registrarActividad("exito", "Acción completada", titulo);
        pushMensaje({ role: "assistant", text: res.mensaje || "Listo, la acción se completó.", animar: true });
        cerrarCanvas();
      } else {
        registrarActividad("error", "La acción falló", res.mensaje);
        setErrorAccion(res.mensaje || "No se pudo completar la acción.");
        setErroresCampo(res.camposConError ?? {});
      }
    } catch (error) {
      const detalle =
        error instanceof Error && error.message.trim() ? error.message.trim() : "No se pudo completar la acción.";
      registrarActividad("error", "La acción falló", detalle);
      setErrorAccion(detalle);
    } finally {
      setEjecutando(false);
    }
  }

  function confirmarAprobacion() {
    if (!aprobacion) {
      return;
    }
    void ejecutar(aprobacion.tool, aprobacion.titulo, aprobacion.params);
  }

  function volverAlFormulario() {
    setAprobacion(null);
  }

  // La conversación y la entrada se comparten entre la burbuja flotante y el Panel a pantalla
  // completa: es la MISMA instancia, así que el progreso no se pierde al pasar de una a otra.
  const conversacion =
    mensajes.length === 0 ? (
      <div className="nova-in mx-auto flex max-w-md flex-col items-center gap-2 pt-2 text-center">
        <NovaAvatar size={44} anillo />
        <p className="font-brand text-base font-bold" style={{ color: "var(--fc-color-text)" }}>
          Hola, soy Nova ✨
        </p>
        <p className="text-xs" style={{ color: "var(--fc-color-text-muted)" }}>
          Tu copiloto del ERP. Pregúntame por existencias, ventas, compras o terceros, o pídeme crear
          un tercero o actualizar un precio. Actúo con tus permisos.
        </p>
        <div className="mt-0.5 flex w-full flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:justify-center">
          {EJEMPLOS.map((ejemplo) => (
            <button
              key={ejemplo}
              type="button"
              onClick={() => void enviar(ejemplo)}
              className="nova-chip rounded-pill px-3 py-2 text-left text-xs transition-all"
              style={{
                border: "1px solid var(--fc-color-border)",
                background: "var(--fc-color-surface-raised)",
                color: "var(--fc-color-text)",
              }}
            >
              {ejemplo}
            </button>
          ))}
        </div>
      </div>
    ) : (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-2.5">
        {mensajes.map((mensaje, indice) => (
          <Burbuja key={indice} mensaje={mensaje} etiquetas={etiquetas} />
        ))}
        {enviando && <Pensando />}
      </div>
    );

  const entradaUi = (
    <div className="px-3 py-2.5" style={{ borderTop: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}>
      <div className="mx-auto flex w-full max-w-3xl items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void adjuntar(file);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={leyendoAdjunto}
          className="grid h-9 w-9 flex-none place-items-center rounded-control transition-colors disabled:opacity-40"
          style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)", color: "var(--fc-color-text-muted)" }}
          aria-label="Adjuntar una factura (imagen o PDF)"
          title="Adjuntar una factura para que Nova la lea"
        >
          {leyendoAdjunto ? (
            <span className="nova-think inline-block h-2 w-2 rounded-full" style={{ background: "var(--fc-color-action)" }} />
          ) : (
            <IconoClip className="h-4 w-4" />
          )}
        </button>
        <textarea
          value={entrada}
          onChange={(e) => setEntrada(e.target.value)}
          onKeyDown={alTeclear}
          rows={1}
          maxLength={4000}
          placeholder="Pregúntale a Nova…"
          className="max-h-28 flex-1 resize-none rounded-control px-3 py-2 text-sm outline-none"
          style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)", color: "var(--fc-color-text)" }}
        />
        <button
          type="button"
          onClick={() => void enviar(entrada)}
          disabled={enviando || !entrada.trim()}
          className="grid h-9 w-9 flex-none place-items-center rounded-control transition-transform hover:scale-105 disabled:opacity-40"
          style={{ background: NOVA_SOLID, color: "#ffffff" }}
          aria-label="Enviar"
        >
          <IconoEnviar className="h-4 w-4" />
        </button>
      </div>
      <p className="mx-auto mt-1.5 w-full max-w-3xl text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
        Nova puede equivocarse; verifica los datos importantes.
      </p>
    </div>
  );

  const canvas =
    accion != null ? (
      <Canvas
        modo={enPanel ? "panel" : "flotante"}
        accion={accion}
        valores={valores}
        onCampo={(clave, valor) => {
          setValores((prev) => ({ ...prev, [clave]: valor }));
          // Al editar un campo con error, se limpia su aviso (el usuario lo está corrigiendo).
          setErroresCampo((prev) => {
            if (!(clave in prev)) return prev;
            const { [clave]: _, ...resto } = prev;
            return resto;
          });
        }}
        lineas={lineas}
        onLineas={setLineas}
        erroresCampo={erroresCampo}
        ejecutando={ejecutando}
        error={errorAccion}
        onConfirmar={solicitarAprobacion}
        onCancelar={cerrarCanvas}
      />
    ) : null;

  const dialogoAprobacion =
    aprobacion != null ? (
      <AprobacionDialog
        titulo={aprobacion.titulo}
        tool={aprobacion.tool}
        params={aprobacion.params}
        ejecutando={ejecutando}
        onConfirmar={confirmarAprobacion}
        onVolver={volverAlFormulario}
      />
    ) : null;

  // En el Panel: Nova a pantalla completa dentro de la página (portal). El chat llena el hueco y
  // el canvas se abre a su lado; sin burbuja ni lanzador.
  if (enPanel && host) {
    return (
      <>
        <style>{NOVA_CSS}</style>
        {createPortal(
          <div className="flex h-full min-h-0 gap-3">
            <section
              className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-panel"
              style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)" }}
              aria-label="Nova, asistente de IA"
            >
              <div className="flex-1 overflow-y-auto px-4 py-3" style={{ background: "var(--fc-color-surface)" }}>
                {conversacion}
                <div ref={finRef} />
              </div>
              {entradaUi}
            </section>
            {canvas}
          </div>,
          host,
        )}
        {dialogoAprobacion}
      </>
    );
  }

  // Fuera del Panel: la burbuja flotante de siempre, con la misma conversación intacta.
  return (
    <>
      <style>{NOVA_CSS}</style>

      {abierto && canvas}
      {dialogoAprobacion}

      {abierto && (
        <section
          className="nova-panel fixed bottom-24 right-6 z-50 flex h-[70vh] max-h-[640px] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-panel shadow-2xl"
          style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)" }}
          aria-label="Nova, asistente de IA"
        >
          <header className="relative flex items-center gap-3 px-4 py-3" style={{ background: GRAD }}>
            <NovaAvatar size={38} anillo />
            <div className="flex flex-col">
              <span className="font-brand text-base font-bold leading-tight" style={{ color: "#ffffff" }}>
                Nova
              </span>
              <span className="flex items-center gap-1.5 text-2xs" style={{ color: "#ffffff", opacity: 0.9 }}>
                <span className="nova-dot inline-block h-1.5 w-1.5 rounded-full" style={{ background: "#4ade80" }} />
                Copiloto del ERP · en línea
              </span>
            </div>
            <button
              type="button"
              onClick={() => setAbierto(false)}
              className="ml-auto grid h-8 w-8 place-items-center rounded-control transition-colors"
              style={{ color: "#ffffff" }}
              aria-label="Cerrar Nova"
            >
              <IconoCerrar className="h-4 w-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-3" style={{ background: "var(--fc-color-surface)" }}>
            {conversacion}
            <div ref={finRef} />
          </div>

          {entradaUi}
        </section>
      )}

      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="nova-launcher fixed bottom-6 right-6 z-50 grid h-14 w-14 place-items-center rounded-full shadow-lg transition-transform hover:scale-110"
        style={{ background: GRAD }}
        aria-label={abierto ? "Cerrar Nova" : "Abrir Nova"}
      >
        {abierto ? (
          <IconoCerrar className="h-6 w-6" style={{ color: "#ffffff" }} />
        ) : (
          <NovaAvatar size={40} />
        )}
      </button>
    </>
  );
}

/** Lee un archivo como base64 (sin el prefijo data:), para mandarlo al OCR del asistente. */
function leerBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onerror = () => reject(new Error("No se pudo leer el archivo."));
    lector.onload = () => {
      const resultado = typeof lector.result === "string" ? lector.result : "";
      const coma = resultado.indexOf(",");
      resolve(coma >= 0 ? resultado.slice(coma + 1) : resultado);
    };
    lector.readAsDataURL(file);
  });
}

/** Resume una factura leída por OCR en un texto legible para el chat. */
function resumirFactura(f: FacturaExtraida): string {
  const partes: string[] = ["📄 Leí la factura:"];
  if (f.proveedorNombre) partes.push(`• Proveedor: ${f.proveedorNombre}${f.proveedorNit ? ` (NIT ${f.proveedorNit})` : ""}`);
  if (f.numeroFactura) partes.push(`• N.º: ${f.numeroFactura}`);
  if (f.fechaEmision) partes.push(`• Emitida: ${f.fechaEmision}`);
  if (f.total) partes.push(`• Total: ${f.total}`);
  const lineas = (f.lineas ?? []).filter((l) => l.descripcion || l.codigo);
  if (lineas.length > 0) partes.push(`• ${lineas.length} ${lineas.length === 1 ? "línea" : "líneas"} de producto`);
  if (partes.length === 1) partes.push("no pude extraer datos claros; prueba con una imagen más nítida.");
  else partes.push("¿Quieres que la registre como factura de compra?");
  return partes.join("\n");
}

function Burbuja({ mensaje, etiquetas }: { mensaje: Mensaje; etiquetas: Map<string, string> }) {
  const esUsuario = mensaje.role === "user";
  const fuentes =
    mensaje.toolsUsed && mensaje.toolsUsed.length > 0
      ? [...new Set(mensaje.toolsUsed)].map((n) => etiquetas.get(n) ?? n)
      : [];
  return (
    <div className={`nova-in flex items-end gap-2 ${esUsuario ? "flex-row-reverse" : ""}`}>
      {esUsuario ? null : <NovaAvatar size={26} />}
      <div className="flex max-w-[82%] flex-col">
        <div
          className="whitespace-pre-wrap rounded-panel px-3 py-2 text-sm"
          style={
            esUsuario
              ? { background: USER_AZUL, color: "#ffffff" }
              : { border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)", color: "var(--fc-color-text)" }
          }
        >
          {esUsuario ? mensaje.text : <TextoStream id={mensaje.id} texto={mensaje.text} animar={mensaje.animar === true} />}
        </div>
        {fuentes.length > 0 ? <Fuentes fuentes={fuentes} /> : null}
      </div>
    </div>
  );
}

/**
 * Revela el texto de Nova con efecto de escritura (streaming). Anima UNA sola vez por mensaje (se
 * apoya en `yaAnimados`): al cambiar de pestaña o recargar no se vuelve a escribir. Respeta
 * prefers-reduced-motion. Ya completo, lo pinta con formato (negritas, listas, código).
 */
function TextoStream({ id, texto, animar }: { id: number; texto: string; animar: boolean }) {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const debeAnimar = animar && !reduce && !yaAnimados.has(id);
  const [n, setN] = useState(debeAnimar ? 0 : texto.length);
  useEffect(() => {
    if (!debeAnimar || !texto) {
      setN(texto.length);
      return;
    }
    yaAnimados.add(id);
    let i = 0;
    const paso = Math.max(1, Math.ceil(texto.length / 90));
    const intervalo = window.setInterval(() => {
      i += paso;
      if (i >= texto.length) {
        setN(texto.length);
        window.clearInterval(intervalo);
      } else {
        setN(i);
      }
    }, 16);
    return () => window.clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (n >= texto.length) {
    return <Formateo texto={texto} />;
  }
  return (
    <>
      {texto.slice(0, n)}
      <span className="nova-caret" aria-hidden>▍</span>
    </>
  );
}

/**
 * Formato ligero y SEGURO (sin HTML crudo) para las respuestas de Nova: negritas (**), cursiva (*),
 * código (`), viñetas y saltos de línea. Así el modelo puede usar Markdown y no se ven asteriscos.
 */
function Formateo({ texto }: { texto: string }) {
  const lineas = texto.split("\n");
  const bloques: ReactNode[] = [];
  let lista: string[] = [];
  let k = 0;
  const cerrarLista = () => {
    if (lista.length > 0) {
      const items = lista;
      bloques.push(
        <ul key={`ul-${k++}`} className="my-1 ml-4 list-disc space-y-0.5">
          {items.map((it, i) => (
            <li key={i}>{inline(it)}</li>
          ))}
        </ul>,
      );
      lista = [];
    }
  };
  for (const linea of lineas) {
    const vineta = /^\s*[-*•]\s+(.*)$/.exec(linea);
    if (vineta) {
      lista.push(vineta[1] ?? "");
      continue;
    }
    cerrarLista();
    if (linea.trim() === "") {
      bloques.push(<div key={`sp-${k++}`} className="h-1.5" />);
      continue;
    }
    const titulo = /^\s*#{1,6}\s+(.*)$/.exec(linea);
    if (titulo) {
      bloques.push(
        <div key={`h-${k++}`} className="font-semibold">
          {inline(titulo[1] ?? "")}
        </div>,
      );
      continue;
    }
    bloques.push(<div key={`p-${k++}`}>{inline(linea)}</div>);
  }
  cerrarLista();
  return <div className="space-y-0.5">{bloques}</div>;
}

/** Formato en línea: **negrita**, *cursiva*, `código`. Construye nodos React (nada de HTML crudo). */
function inline(texto: string): ReactNode[] {
  const nodos: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|`([^`]+)`)/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > last) {
      nodos.push(texto.slice(last, m.index));
    }
    if (m[2] !== undefined || m[3] !== undefined) {
      nodos.push(<strong key={k++}>{m[2] ?? m[3]}</strong>);
    } else if (m[4] !== undefined || m[5] !== undefined) {
      nodos.push(<em key={k++}>{m[4] ?? m[5]}</em>);
    } else if (m[6] !== undefined) {
      nodos.push(
        <code
          key={k++}
          className="rounded px-1 font-data text-[0.85em]"
          style={{ background: "var(--fc-color-surface)", border: "1px solid var(--fc-color-border)" }}
        >
          {m[6]}
        </code>,
      );
    }
    last = re.lastIndex;
  }
  if (last < texto.length) {
    nodos.push(texto.slice(last));
  }
  return nodos;
}

/** Fuentes/pasos que Nova consultó, plegables (transparencia sin ruido). */
function Fuentes({ fuentes }: { fuentes: string[] }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="mt-1 px-1">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="inline-flex items-center gap-1 text-2xs transition-colors"
        style={{ color: "var(--fc-color-text-muted)" }}
      >
        <span
          className="grid h-3.5 w-3.5 place-items-center rounded-full text-[9px]"
          style={{ background: "var(--fc-color-surface)", border: "1px solid var(--fc-color-border)" }}
        >
          {abierto ? "−" : "+"}
        </span>
        {fuentes.length} {fuentes.length === 1 ? "fuente consultada" : "fuentes consultadas"}
      </button>
      {abierto ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {fuentes.map((f) => (
            <span
              key={f}
              className="rounded-pill px-2 py-0.5 text-2xs"
              style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)", color: "var(--fc-color-text-muted)" }}
            >
              {f}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function humaniza(clave: string): string {
  return clave
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Diálogo de resumen y confirmación de una escritura: muestra lo que se va a crear/actualizar (los
 * campos, en verde) y confirma. Es la última puerta antes de tocar el ERP; el backend valida con tu
 * token. Sin "permitir siempre": siempre lo confirma la persona.
 */
function AprobacionDialog({
  titulo,
  tool,
  params,
  ejecutando,
  onConfirmar,
  onVolver,
}: {
  titulo: string;
  tool: string;
  params: Record<string, unknown>;
  ejecutando: boolean;
  onConfirmar: () => void;
  onVolver: () => void;
}) {
  const esquema = esquemaDe(tool);
  const cta = esquema?.cta ?? "Confirmar";
  const productos: string[] = [];
  const presentaciones: string[] = [];
  for (const campo of esquema?.campos ?? []) {
    if (campo.tipo === "idresuelto") {
      const v = params[campo.key];
      if (typeof v === "string" && v) {
        if (campo.resuelve === "producto") productos.push(v);
        else if (campo.resuelve === "presentacion") presentaciones.push(v);
      }
    }
  }
  const nombres = useNombres({ products: productos, presentations: presentaciones });

  const filas: { label: string; valor: string }[] = [];
  const vistas = new Set<string>();
  const agrega = (key: string, label: string) => {
    const v = params[key];
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
      return;
    }
    const campo = esquema?.campos.find((c) => c.key === key);
    if (campo?.tipo === "lineas-orden" && Array.isArray(v)) {
      const items = v.map((it) => {
        const l = (it ?? {}) as Record<string, unknown>;
        const nombre = String(l["productLabel"] ?? l["productId"] ?? "producto");
        const pres = l["presentationLabel"] ? ` (${String(l["presentationLabel"])})` : "";
        const cant = l["quantity"] ? ` ×${String(l["quantity"])}` : "";
        const precio = l["expectedUnitPrice"] ? ` @ $${String(l["expectedUnitPrice"])}` : "";
        const iva = l["expectedTaxRatePercent"] ? ` IVA ${String(l["expectedTaxRatePercent"])}%` : "";
        const flete = l["expectedFreightPerUnit"] ? ` flete $${String(l["expectedFreightPerUnit"])}` : "";
        return `${nombre}${pres}${cant}${precio}${iva}${flete}`;
      });
      filas.push({ label, valor: items.join("  ·  ") });
      vistas.add(key);
      return;
    }
    let valor = Array.isArray(v) ? v.map((x) => String(x)) : [String(v)];
    if (campo?.tipo === "idresuelto") {
      const id = valor[0] ?? "";
      const nombre =
        campo.resuelve === "producto"
          ? nombres.producto(id)
          : campo.resuelve === "presentacion"
            ? nombres.presentacion(id)
            : undefined;
      valor = [nombre ?? id];
    } else if (campo?.opciones) {
      valor = valor.map((x) => campo.opciones?.find((o) => o.value === x.trim())?.label ?? x.trim());
    }
    filas.push({ label, valor: valor.join(", ") });
    vistas.add(key);
  };
  for (const campo of esquema?.campos ?? []) {
    agrega(campo.key, campo.label);
  }
  for (const [k, v] of Object.entries(params)) {
    if (vistas.has(k)) continue;
    const valor = Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v);
    if (valor) filas.push({ label: humaniza(k), valor });
  }

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center p-4"
      style={{ background: "color-mix(in srgb, var(--fc-color-text) 55%, transparent)" }}
      role="dialog"
      aria-modal="true"
      aria-label={`Aprobar: ${titulo}`}
    >
      <div
        className="nova-in flex w-full max-w-md flex-col overflow-hidden rounded-panel shadow-2xl"
        style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)" }}
      >
        <header className="flex items-center gap-2.5 px-4 py-3" style={{ background: GRAD }}>
          <span
            className="grid h-7 w-7 flex-none place-items-center rounded-control"
            style={{ background: "color-mix(in srgb, #ffffff 20%, transparent)" }}
          >
            <IconoEscudo className="h-4 w-4" style={{ color: "#ffffff" }} />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="text-2xs font-semibold uppercase tracking-wide" style={{ color: "#ffffff", opacity: 0.9 }}>
              Resumen y confirmación
            </span>
            <span className="truncate font-brand text-sm font-bold" style={{ color: "#ffffff" }}>
              {titulo}
            </span>
          </div>
        </header>

        <div className="max-h-[50vh] overflow-y-auto px-4 py-3">
          <p className="mb-2 font-data text-2xs uppercase tracking-wider" style={{ color: "var(--fc-color-text-muted)" }}>
            Cambios propuestos
          </p>
          <div className="overflow-hidden rounded-control" style={{ border: "1px solid var(--fc-color-border)" }}>
            {filas.length === 0 ? (
              <div className="px-2.5 py-2 text-xs" style={{ color: "var(--fc-color-text-muted)" }}>
                Sin campos.
              </div>
            ) : (
              filas.map((f, i) => (
                <div
                  key={f.label}
                  className="flex items-baseline gap-2 px-2.5 py-1.5 font-data text-xs"
                  style={{
                    background: "color-mix(in srgb, #4ade80 9%, var(--fc-color-surface))",
                    borderTop: i === 0 ? "none" : "1px solid var(--fc-color-border)",
                  }}
                >
                  <span className="flex-none font-bold" style={{ color: "#16a34a" }}>
                    +
                  </span>
                  <span className="flex-none" style={{ color: "var(--fc-color-text-muted)" }}>
                    {f.label}:
                  </span>
                  <span className="break-all" style={{ color: "var(--fc-color-text)" }}>
                    {f.valor}
                  </span>
                </div>
              ))
            )}
          </div>
          <p className="mt-3 text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
            Se ejecuta con tus permisos; el backend valida con tu sesión.
          </p>
        </div>

        <div
          className="flex items-center gap-2 px-4 py-3"
          style={{ borderTop: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
        >
          <button
            type="button"
            disabled={ejecutando}
            onClick={onVolver}
            className="rounded-control px-3 py-2 text-sm font-medium disabled:opacity-40"
            style={{ border: "1px solid var(--fc-color-border)", color: "var(--fc-color-text)", background: "var(--fc-color-surface)" }}
          >
            Volver
          </button>
          <button
            type="button"
            disabled={ejecutando}
            onClick={onConfirmar}
            className="ml-auto flex items-center gap-2 rounded-control px-4 py-2 text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-50"
            style={{ background: GRAD, color: "#ffffff" }}
          >
            {ejecutando ? (
              <>
                <span className="nova-think inline-block h-2 w-2 rounded-full" style={{ background: "#ffffff" }} />
                Creando…
              </>
            ) : (
              cta
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function IconoEscudo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <ShieldCheck className={className} style={style} weight="fill" aria-hidden />;
}

function Pensando() {
  return (
    <div className="nova-in flex items-end gap-2">
      <NovaAvatar size={26} />
      <div
        className="flex items-center gap-1 rounded-panel px-3 py-2.5"
        style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="nova-think inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--fc-color-action)", animationDelay: `${i * 0.16}s` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Lienzo de Nova: la ventana anexa que se abre al lado del chat cuando Nova propone una escritura.
 * Renderiza un FORMULARIO GUIADO por esquema (desplegables, chips, precio, fecha) para que rellenar
 * sea rápido e intuitivo; Nova pre-rellena lo que ya sabe y la persona completa el resto. Nada se
 * ejecuta hasta confirmar (el gatillo humano; el backend valida con tu token). Si la acción no
 * tiene esquema, cae a un formulario genérico clave/valor.
 */
function Canvas({
  accion,
  valores,
  onCampo,
  lineas,
  onLineas,
  erroresCampo,
  ejecutando,
  error,
  onConfirmar,
  onCancelar,
  modo = "flotante",
}: {
  accion: AccionPendiente;
  valores: Record<string, string | string[]>;
  onCampo: (clave: string, valor: string | string[]) => void;
  lineas: LineaOrden[];
  onLineas: Dispatch<SetStateAction<LineaOrden[]>>;
  erroresCampo: Record<string, string>;
  ejecutando: boolean;
  error: string | null;
  onConfirmar: () => void;
  onCancelar: () => void;
  modo?: "flotante" | "panel";
}) {
  const esquema = esquemaDe(accion.tool);
  // Flotante: fijo a la izquierda de la burbuja. Panel: una columna al lado del chat, sin fijar.
  const claseSeccion =
    modo === "panel"
      ? "nova-canvas flex h-full w-[22rem] max-w-[42vw] flex-none flex-col overflow-hidden rounded-panel shadow-sm"
      : "nova-canvas fixed bottom-24 right-[calc(400px+2.5rem)] z-50 flex h-[70vh] max-h-[640px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-panel shadow-2xl";

  // Ids que el formulario muestra como NOMBRE real (no UUID) → resolverlos en lote.
  const productos: string[] = [];
  const presentaciones: string[] = [];
  for (const campo of esquema?.campos ?? []) {
    if (campo.tipo === "idresuelto") {
      const v = valores[campo.key];
      const id = typeof v === "string" ? v : "";
      if (id && campo.resuelve === "producto") productos.push(id);
      else if (id && campo.resuelve === "presentacion") presentaciones.push(id);
    }
  }
  const nombres = useNombres({ products: productos, presentations: presentaciones });

  // Las líneas se validan aparte (viven en estado propio): la orden exige al menos una completa,
  // y sus términos comerciales completos (precio + IVA) o vacíos —nunca a medias, que el ERP rechaza.
  const faltanLineas = esquemaTieneLineas(accion.tool) && lineas.filter(lineaCompleta).length === 0;
  const terminosAMedias = esquemaTieneLineas(accion.tool) && hayTerminosParciales(lineas);
  const faltan = [
    ...faltantes(accion.tool, valores),
    ...(faltanLineas ? ["Productos"] : []),
    ...(terminosAMedias ? ["Precio + IVA de cada producto con precio"] : []),
  ];
  const titulo = esquema?.titulo ?? accion.label;
  const intro = esquema?.intro ?? accion.resumen;
  const cta = esquema?.cta ?? "Confirmar";

  return (
    <section
      className={claseSeccion}
      style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)" }}
      aria-label={`Lienzo de Nova: ${titulo}`}
    >
      <header
        className="flex items-center gap-2.5 px-4 py-3"
        style={{ borderBottom: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
      >
        <span className="grid h-7 w-7 flex-none place-items-center rounded-control" style={{ background: GRAD }}>
          <IconoLienzo className="h-4 w-4" style={{ color: "#ffffff" }} />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-2xs font-semibold uppercase tracking-wide" style={{ color: "var(--fc-color-text-muted)" }}>
            Lienzo de Nova
          </span>
          <span className="truncate font-brand text-sm font-bold leading-tight" style={{ color: "var(--fc-color-text)" }}>
            {titulo}
          </span>
        </div>
        <button
          type="button"
          onClick={onCancelar}
          disabled={ejecutando}
          className="ml-auto grid h-7 w-7 flex-none place-items-center rounded-control disabled:opacity-40"
          style={{ color: "var(--fc-color-text-muted)" }}
          aria-label="Cerrar lienzo"
        >
          <IconoCerrar className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {intro ? (
          <p
            className="mb-3 rounded-control px-3 py-2 text-xs"
            style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)", color: "var(--fc-color-text-muted)" }}
          >
            {intro}
          </p>
        ) : null}
        <div className="flex flex-col gap-3">
          {esquema
            ? esquema.campos.map((campo) =>
                campo.tipo === "lineas-orden" ? (
                  <LineasOrdenControl
                    key={campo.key}
                    campo={campo}
                    lineas={lineas}
                    onLineas={onLineas}
                    ejecutando={ejecutando}
                  />
                ) : (
                  <div key={campo.key} className="flex flex-col gap-1">
                    <CampoControl
                      campo={campo}
                      valor={valores[campo.key]}
                      onCampo={onCampo}
                      ejecutando={ejecutando}
                      nombres={nombres}
                    />
                    {erroresCampo[campo.key] ? (
                      <span className="text-2xs" style={{ color: "var(--fc-color-danger)" }}>
                        {erroresCampo[campo.key]}
                      </span>
                    ) : null}
                  </div>
                ),
              )
            : Object.keys(valores).map((clave) => (
                <CampoGenerico
                  key={clave}
                  clave={clave}
                  valor={valores[clave]}
                  onCampo={onCampo}
                  ejecutando={ejecutando}
                />
              ))}
        </div>
        {faltan.length > 0 ? (
          <p className="mt-3 text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
            Faltan por rellenar: {faltan.join(", ")}.
          </p>
        ) : null}
        {error ? (
          <p
            className="mt-3 rounded-control px-3 py-2 text-xs"
            style={{ background: "color-mix(in srgb, var(--fc-color-danger) 12%, transparent)", color: "var(--fc-color-danger)" }}
          >
            {error}
          </p>
        ) : null}
      </div>

      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderTop: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
      >
        <button
          type="button"
          onClick={onCancelar}
          disabled={ejecutando}
          className="rounded-control px-3 py-2 text-sm font-medium disabled:opacity-40"
          style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface)", color: "var(--fc-color-text)" }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={ejecutando || faltan.length > 0}
          className="ml-auto flex items-center gap-2 rounded-control px-4 py-2 text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-50"
          style={{ background: GRAD, color: "#ffffff" }}
        >
          {ejecutando ? (
            <>
              <span className="nova-think inline-block h-2 w-2 rounded-full" style={{ background: "#ffffff" }} />
              Ejecutando…
            </>
          ) : (
            <>
              <IconoCheck className="h-4 w-4" />
              {cta}
            </>
          )}
        </button>
      </div>
    </section>
  );
}

const INPUT_CLASE = "rounded-control px-2.5 py-1.5 text-sm outline-none disabled:opacity-60";
const INPUT_ESTILO: React.CSSProperties = {
  border: "1px solid var(--fc-color-border)",
  background: "var(--fc-color-surface)",
  color: "var(--fc-color-text)",
};

/** Una etiqueta de campo, con asterisco si es obligatorio. */
function Etiqueta({ campo }: { campo: CampoForm }) {
  return (
    <span className="text-2xs font-medium" style={{ color: "var(--fc-color-text-muted)" }}>
      {campo.label}
      {campo.requerido ? <span style={{ color: "var(--fc-color-action)" }}> *</span> : null}
    </span>
  );
}

function Ayuda({ texto }: { texto: string }) {
  return (
    <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
      {texto}
    </span>
  );
}

/** El control adecuado para un campo del esquema (desplegable, chips, precio, fecha, texto…). */
function CampoControl({
  campo,
  valor,
  onCampo,
  ejecutando,
  nombres,
}: {
  campo: CampoForm;
  valor: string | string[] | undefined;
  onCampo: (clave: string, valor: string | string[]) => void;
  ejecutando: boolean;
  nombres: ReturnType<typeof useNombres>;
}) {
  const texto = typeof valor === "string" ? valor : "";
  const lista = Array.isArray(valor) ? valor : [];

  if (campo.tipo === "tercero-buscar") {
    return <TerceroControl campo={campo} onCampo={onCampo} />;
  }

  if (campo.tipo === "idresuelto") {
    const nombre =
      campo.resuelve === "producto"
        ? nombres.producto(texto)
        : campo.resuelve === "presentacion"
          ? nombres.presentacion(texto)
          : undefined;
    const mostrado = nombre ?? (texto ? `· ${texto.slice(0, 8)}…` : "—");
    return (
      <label className="flex flex-col gap-1">
        <Etiqueta campo={campo} />
        <div
          className="rounded-control px-2.5 py-1.5 text-sm"
          style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)", color: "var(--fc-color-text)" }}
        >
          {mostrado}
        </div>
      </label>
    );
  }

  if (campo.tipo === "select-remoto" || campo.tipo === "multiselect-remoto") {
    return campo.fuente ? (
      <CampoRemoto campo={campo} valor={valor} onCampo={onCampo} ejecutando={ejecutando} />
    ) : null;
  }

  if (campo.tipo === "select") {
    return (
      <SelectVisual campo={campo} valor={texto} opciones={campo.opciones ?? []} onCampo={onCampo} ejecutando={ejecutando} />
    );
  }

  if (campo.tipo === "multiselect") {
    return (
      <ChipsVisual campo={campo} valores={lista} opciones={campo.opciones ?? []} onCampo={onCampo} ejecutando={ejecutando} />
    );
  }

  if (campo.tipo === "currency") {
    return (
      <label className="flex flex-col gap-1">
        <Etiqueta campo={campo} />
        <div className="flex items-center rounded-control" style={INPUT_ESTILO}>
          <span className="pl-2.5 text-sm" style={{ color: "var(--fc-color-text-muted)" }}>
            $
          </span>
          <input
            value={texto}
            onChange={(e) => onCampo(campo.key, e.target.value.replace(/[^0-9.]/g, ""))}
            disabled={ejecutando}
            inputMode="decimal"
            placeholder={campo.placeholder ?? ""}
            className="min-w-0 flex-1 bg-transparent px-2 py-1.5 text-sm outline-none disabled:opacity-60"
            style={{ color: "var(--fc-color-text)" }}
          />
        </div>
        {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
      </label>
    );
  }

  const htmlType =
    campo.tipo === "date"
      ? "date"
      : campo.tipo === "email"
        ? "email"
        : campo.tipo === "tel"
          ? "tel"
          : campo.tipo === "password"
            ? "password"
            : "text";
  return (
    <label className="flex flex-col gap-1">
      <Etiqueta campo={campo} />
      <input
        type={htmlType}
        value={texto}
        onChange={(e) => onCampo(campo.key, e.target.value)}
        disabled={ejecutando}
        placeholder={campo.placeholder ?? ""}
        {...(campo.tipo === "number" ? { inputMode: "numeric" as const } : {})}
        className={INPUT_CLASE}
        style={INPUT_ESTILO}
      />
      {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
    </label>
  );
}

/** Desplegable (opciones locales o remotas), reutilizado por 'select' y 'select-remoto'. */
function SelectVisual({
  campo,
  valor,
  opciones,
  onCampo,
  ejecutando,
  cargando,
}: {
  campo: CampoForm;
  valor: string;
  opciones: OpcionCampo[];
  onCampo: (clave: string, valor: string | string[]) => void;
  ejecutando: boolean;
  cargando?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <Etiqueta campo={campo} />
      <select
        value={valor}
        onChange={(e) => onCampo(campo.key, e.target.value)}
        disabled={ejecutando || cargando === true}
        className={INPUT_CLASE}
        style={INPUT_ESTILO}
      >
        <option value="">{cargando ? "Cargando…" : "Elige…"}</option>
        {opciones.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
    </label>
  );
}

/** Chips de selección múltiple (opciones locales o remotas), reutilizado por 'multiselect(-remoto)'. */
function ChipsVisual({
  campo,
  valores,
  opciones,
  onCampo,
  ejecutando,
  cargando,
}: {
  campo: CampoForm;
  valores: string[];
  opciones: OpcionCampo[];
  onCampo: (clave: string, valor: string | string[]) => void;
  ejecutando: boolean;
  cargando?: boolean;
}) {
  const alternar = (v: string) =>
    onCampo(campo.key, valores.includes(v) ? valores.filter((x) => x !== v) : [...valores, v]);
  return (
    <div className="flex flex-col gap-1">
      <Etiqueta campo={campo} />
      {cargando ? (
        <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
          Cargando…
        </span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {opciones.length === 0 ? (
            <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
              Sin opciones.
            </span>
          ) : (
            opciones.map((o) => {
              const activo = valores.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={ejecutando}
                  onClick={() => alternar(o.value)}
                  className="rounded-pill px-2.5 py-1 text-xs transition-all disabled:opacity-60"
                  style={
                    activo
                      ? { background: GRAD, color: "#ffffff", border: "1px solid transparent" }
                      : { background: "var(--fc-color-surface)", color: "var(--fc-color-text)", border: "1px solid var(--fc-color-border)" }
                  }
                >
                  {o.label}
                </button>
              );
            })
          )}
        </div>
      )}
      {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
    </div>
  );
}

/** Campo con opciones que se cargan en vivo del ERP (roles, sedes): desplegable o chips. */
function CampoRemoto({
  campo,
  valor,
  onCampo,
  ejecutando,
}: {
  campo: CampoForm;
  valor: string | string[] | undefined;
  onCampo: (clave: string, valor: string | string[]) => void;
  ejecutando: boolean;
}) {
  const { opciones, cargando } = useOpcionesRemotas(campo.fuente ?? "roles-usuario");
  if (campo.tipo === "multiselect-remoto") {
    return (
      <ChipsVisual
        campo={campo}
        valores={Array.isArray(valor) ? valor : []}
        opciones={opciones}
        onCampo={onCampo}
        ejecutando={ejecutando}
        cargando={cargando}
      />
    );
  }
  return (
    <SelectVisual
      campo={campo}
      valor={typeof valor === "string" ? valor : ""}
      opciones={opciones}
      onCampo={onCampo}
      ejecutando={ejecutando}
      cargando={cargando}
    />
  );
}

/** Buscador escribe-y-elige de un tercero (proveedor/cliente); guarda su UUID en el campo. */
function TerceroControl({
  campo,
  onCampo,
}: {
  campo: CampoForm;
  onCampo: (clave: string, valor: string | string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Etiqueta campo={campo} />
      <SelectorTercero
        label={campo.label}
        ocultarEtiqueta
        {...(campo.rolTercero ? { rol: campo.rolTercero } : {})}
        onSelect={(t) => onCampo(campo.key, t?.id ?? "")}
      />
      {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
    </div>
  );
}

/** Líneas repetibles de producto de una orden de compra (viven en estado propio del lienzo). */
function LineasOrdenControl({
  campo,
  lineas,
  onLineas,
  ejecutando,
}: {
  campo: CampoForm;
  lineas: LineaOrden[];
  onLineas: Dispatch<SetStateAction<LineaOrden[]>>;
  ejecutando: boolean;
}) {
  // Clave producto+presentación por línea, para marcar como duplicada la segunda aparición.
  const claves = lineas.map((l) =>
    l.productId && l.presentationId ? `${l.productId}:${l.presentationId}` : "",
  );
  // Total estimado: suma de cantidad × precio de las líneas completas con precio (best-effort).
  let total = 0;
  let hayPrecios = false;
  for (const l of lineas) {
    if (!lineaCompleta(l) || l.expectedUnitPrice.trim() === "") continue;
    const q = Number(l.quantity);
    const p = Number(l.expectedUnitPrice);
    if (Number.isFinite(q) && Number.isFinite(p)) {
      total += q * p;
      hayPrecios = true;
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <Etiqueta campo={campo} />
      {lineas.length === 0 ? (
        <p
          className="rounded-control px-3 py-2 text-2xs"
          style={{ border: "1px dashed var(--fc-color-border)", color: "var(--fc-color-text-muted)" }}
        >
          Aún no hay productos. Agrega el primero.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {lineas.map((l, i) => (
            <LineaFila
              key={l.uid}
              linea={l}
              onLineas={onLineas}
              ejecutando={ejecutando}
              indice={i}
              duplicada={claves[i] !== "" && claves.indexOf(claves[i] ?? "") < i}
            />
          ))}
        </div>
      )}
      {hayPrecios ? (
        <div
          className="flex items-baseline justify-between rounded-control px-3 py-2 text-xs font-data"
          style={{ background: "var(--fc-color-surface-raised)", border: "1px solid var(--fc-color-border)" }}
        >
          <span style={{ color: "var(--fc-color-text-muted)" }}>Total estimado</span>
          <span className="font-semibold" style={{ color: "var(--fc-color-text)" }}>
            ${total.toLocaleString("es-CO", { maximumFractionDigits: 2 })}
          </span>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => onLineas((prev) => [...prev, lineaVacia()])}
        disabled={ejecutando}
        className="flex items-center justify-center gap-1.5 rounded-control px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50"
        style={{ border: "1px solid var(--fc-color-action)", color: "var(--fc-color-action)", background: "transparent" }}
      >
        <IconoMas className="h-3.5 w-3.5" />
        Agregar producto
      </button>
      {campo.ayuda ? <Ayuda texto={campo.ayuda} /> : null}
    </div>
  );
}

/** Una fila de la orden: busca el producto, elige su presentación e indica cantidad y precio. */
function LineaFila({
  linea,
  onLineas,
  ejecutando,
  indice,
  duplicada,
}: {
  linea: LineaOrden;
  onLineas: Dispatch<SetStateAction<LineaOrden[]>>;
  ejecutando: boolean;
  indice: number;
  duplicada: boolean;
}) {
  const api = useApi();
  const { tenantId } = useOrg();
  const [presentaciones, setPresentaciones] = useState<Presentacion[]>([]);
  const [cargandoPres, setCargandoPres] = useState(false);
  // Los términos esperados son opcionales y avanzados: se ocultan por defecto (línea limpia) y
  // sólo se muestran si el usuario los pide o la línea ya trae alguno.
  const [verTerminos, setVerTerminos] = useState(
    () =>
      linea.expectedUnitPrice.trim() !== "" ||
      linea.expectedTaxRatePercent.trim() !== "" ||
      linea.expectedFreightPerUnit.trim() !== "",
  );

  const parchar = (patch: Partial<LineaOrden>) =>
    onLineas((prev) => prev.map((l) => (l.uid === linea.uid ? { ...l, ...patch } : l)));

  async function onProducto(p: ProductoBuscado | null) {
    if (!p?.productId) {
      parchar({ productId: "", productLabel: "", presentationId: "", presentationLabel: "" });
      setPresentaciones([]);
      return;
    }
    const pid = p.productId;
    parchar({ productId: pid, productLabel: p.name ?? p.sku ?? "", presentationId: "", presentationLabel: "" });
    setCargandoPres(true);
    setPresentaciones([]);
    try {
      const { data, response } = await api.GET("/api/v1/tenants/{tenantId}/products/{productId}", {
        params: { path: { tenantId, productId: pid } },
      });
      const pres = response.ok ? (data?.presentations ?? []) : [];
      setPresentaciones(pres);
      const unica = pres[0];
      if (pres.length === 1 && unica?.id) {
        parchar({ presentationId: unica.id, presentationLabel: unica.name ?? unica.code ?? "" });
      }
    } finally {
      setCargandoPres(false);
    }
  }

  function onPresentacion(id: string) {
    const p = presentaciones.find((x) => x.id === id);
    parchar({ presentationId: id, presentationLabel: p?.name ?? p?.code ?? "" });
  }

  // Sólo dígitos y UN punto decimal (el ERP exige un decimal bien formado).
  const filtroNumero = (v: string) => {
    const limpio = v.replace(/[^0-9.]/g, "");
    const i = limpio.indexOf(".");
    return i === -1 ? limpio : limpio.slice(0, i + 1) + limpio.slice(i + 1).replace(/\./g, "");
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-control p-2.5"
      style={{ border: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
    >
      <div className="flex items-center justify-between">
        <span className="text-2xs font-semibold" style={{ color: "var(--fc-color-text-muted)" }}>
          Producto {indice + 1}
        </span>
        <button
          type="button"
          onClick={() => onLineas((prev) => prev.filter((l) => l.uid !== linea.uid))}
          disabled={ejecutando}
          className="text-2xs font-medium disabled:opacity-50"
          style={{ color: "var(--fc-color-danger)" }}
        >
          Quitar
        </button>
      </div>
      <SelectorProducto ocultarEtiqueta onSelect={onProducto} />
      {duplicada ? (
        <span className="text-2xs" style={{ color: "var(--fc-color-warning, #b45309)" }}>
          Ya agregaste este producto y presentación en otra línea.
        </span>
      ) : null}
      <label className="flex flex-col gap-1">
        <span className="text-2xs font-medium" style={{ color: "var(--fc-color-text-muted)" }}>
          Presentación
        </span>
        <select
          value={linea.presentationId}
          onChange={(e) => onPresentacion(e.target.value)}
          disabled={ejecutando || linea.productId === "" || presentaciones.length === 0}
          className={INPUT_CLASE}
          style={INPUT_ESTILO}
        >
          <option value="">
            {cargandoPres
              ? "Cargando…"
              : linea.productId === ""
                ? "Elige un producto primero"
                : presentaciones.length === 0
                  ? "Sin presentaciones"
                  : "Elige…"}
          </option>
          {presentaciones.map((p) => (
            <option key={p.id ?? ""} value={p.id ?? ""}>
              {[p.name, p.code].filter(Boolean).join(" · ") || (p.id ?? "")}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-2xs font-medium" style={{ color: "var(--fc-color-text-muted)" }}>
          Cantidad
        </span>
        <input
          value={linea.quantity}
          onChange={(e) => parchar({ quantity: filtroNumero(e.target.value) })}
          disabled={ejecutando}
          inputMode="decimal"
          placeholder="0"
          className={INPUT_CLASE}
          style={INPUT_ESTILO}
        />
      </label>
      {verTerminos ? (
        <div className="flex flex-col gap-1.5 rounded-control p-2" style={{ background: "var(--fc-color-surface)", border: "1px solid var(--fc-color-border)" }}>
          <div className="flex items-center justify-between">
            <span className="text-2xs font-medium" style={{ color: "var(--fc-color-text-muted)" }}>
              Precio esperado (opcional)
            </span>
            <button
              type="button"
              onClick={() => {
                parchar({ expectedUnitPrice: "", expectedTaxRatePercent: "", expectedFreightPerUnit: "" });
                setVerTerminos(false);
              }}
              disabled={ejecutando}
              className="text-2xs font-medium disabled:opacity-50"
              style={{ color: "var(--fc-color-text-muted)" }}
            >
              Sin precio
            </button>
          </div>
          <label className="flex flex-col gap-0.5">
            <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>Precio unitario</span>
            <div className="flex items-center rounded-control" style={INPUT_ESTILO}>
              <span className="pl-2 text-xs" style={{ color: "var(--fc-color-text-muted)" }}>$</span>
              <input
                value={linea.expectedUnitPrice}
                onChange={(e) => parchar({ expectedUnitPrice: filtroNumero(e.target.value) })}
                disabled={ejecutando}
                inputMode="decimal"
                placeholder="0"
                className="min-w-0 flex-1 bg-transparent px-1.5 py-1.5 text-sm outline-none disabled:opacity-60"
                style={{ color: "var(--fc-color-text)" }}
              />
            </div>
          </label>
          <div className="flex gap-2">
            <label className="flex flex-1 flex-col gap-0.5">
              <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>IVA %</span>
              <input
                value={linea.expectedTaxRatePercent}
                onChange={(e) => parchar({ expectedTaxRatePercent: filtroNumero(e.target.value) })}
                disabled={ejecutando}
                inputMode="decimal"
                placeholder="p. ej. 19"
                className={INPUT_CLASE}
                style={INPUT_ESTILO}
              />
            </label>
            <label className="flex flex-1 flex-col gap-0.5">
              <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>Flete por unidad</span>
              <div className="flex items-center rounded-control" style={INPUT_ESTILO}>
                <span className="pl-2 text-xs" style={{ color: "var(--fc-color-text-muted)" }}>$</span>
                <input
                  value={linea.expectedFreightPerUnit}
                  onChange={(e) => parchar({ expectedFreightPerUnit: filtroNumero(e.target.value) })}
                  disabled={ejecutando}
                  inputMode="decimal"
                  placeholder="0"
                  className="min-w-0 flex-1 bg-transparent px-1.5 py-1.5 text-sm outline-none disabled:opacity-60"
                  style={{ color: "var(--fc-color-text)" }}
                />
              </div>
            </label>
          </div>
          {terminosParciales(linea) ? (
            <span className="text-2xs" style={{ color: "var(--fc-color-warning, #b45309)" }}>
              Con precio, indica también el IVA %. El flete es opcional (0 si no hay).
            </span>
          ) : (
            <span className="text-2xs" style={{ color: "var(--fc-color-text-muted)" }}>
              Estimado para la orden; el precio real se fija al facturar.
            </span>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setVerTerminos(true)}
          disabled={ejecutando}
          className="self-start text-2xs font-medium disabled:opacity-50"
          style={{ color: "var(--fc-color-action)" }}
        >
          + Precio esperado (opcional)
        </button>
      )}
    </div>
  );
}

/** Campo genérico (acción sin esquema): una caja de texto por clave, con la clave humanizada. */
function CampoGenerico({
  clave,
  valor,
  onCampo,
  ejecutando,
}: {
  clave: string;
  valor: string | string[] | undefined;
  onCampo: (clave: string, valor: string | string[]) => void;
  ejecutando: boolean;
}) {
  const texto = typeof valor === "string" ? valor : Array.isArray(valor) ? valor.join(", ") : "";
  const etiqueta = clave
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-2xs font-medium" style={{ color: "var(--fc-color-text-muted)" }}>
        {etiqueta}
      </span>
      <input
        value={texto}
        onChange={(e) => onCampo(clave, e.target.value)}
        disabled={ejecutando}
        className={INPUT_CLASE}
        style={INPUT_ESTILO}
      />
    </label>
  );
}

/** Avatar de Nova: su mascota (robot naranja sobre azul). Es la imagen de perfil oficial de Nova. */
function NovaAvatar({ size = 32, anillo = false }: { size?: number; anillo?: boolean }) {
  return (
    <span
      className="nova-avatar relative inline-block flex-none overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        background: "#3f88d6",
        boxShadow: anillo
          ? "0 0 0 2px #ffffff, 0 4px 14px rgba(249,115,22,0.35)"
          : "0 2px 10px rgba(234,88,12,0.30)",
      }}
      aria-hidden
    >
      <img
        src={novaRobot}
        alt=""
        aria-hidden
        draggable={false}
        className="h-full w-full object-cover"
        style={{ objectPosition: "50% 22%" }}
      />
    </span>
  );
}

// Iconos de Nova con Phosphor (@phosphor-icons/react). Se mantienen los nombres IconoXxx como
// envoltorio fino sobre el glifo Phosphor: así todas las llamadas del componente quedan igual y
// cualquier icono nuevo se toma también de Phosphor.
function IconoEnviar({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <PaperPlaneRight className={className} style={style} weight="fill" aria-hidden />;
}

function IconoCerrar({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <X className={className} style={style} weight="bold" aria-hidden />;
}

function IconoClip({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <Paperclip className={className} style={style} weight="regular" aria-hidden />;
}

function IconoLienzo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <SquaresFour className={className} style={style} weight="bold" aria-hidden />;
}

function IconoCheck({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <Check className={className} style={style} weight="bold" aria-hidden />;
}

function IconoMas({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <Plus className={className} style={style} weight="bold" aria-hidden />;
}

const NOVA_CSS = `
.nova-panel{animation:nova-slide .28s cubic-bezier(.16,1,.3,1) both;}
.nova-canvas{animation:nova-slide-l .3s cubic-bezier(.16,1,.3,1) both;}
.nova-in{animation:nova-up .32s cubic-bezier(.16,1,.3,1) both;}
.nova-chip:hover{transform:translateY(-1px);border-color:var(--fc-color-action)!important;}
.nova-launcher{animation:nova-drop .5s cubic-bezier(.34,1.56,.64,1) both;}
.nova-avatar svg{animation:nova-spark 3.5s ease-in-out infinite;}
.nova-dot{animation:nova-pulse 2s ease-in-out infinite;}
.nova-think{animation:nova-bounce 1s ease-in-out infinite;}
.nova-caret{display:inline-block;margin-left:1px;animation:nova-blink 1s step-end infinite;color:var(--fc-color-action);font-weight:400;}
@keyframes nova-slide{from{opacity:0;transform:translateY(12px) scale(.98);}to{opacity:1;transform:none;}}
@keyframes nova-slide-l{from{opacity:0;transform:translateX(16px) scale(.98);}to{opacity:1;transform:none;}}
@keyframes nova-up{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
@keyframes nova-pop{from{opacity:0;transform:scale(.6);}to{opacity:1;transform:none;}}
@keyframes nova-drop{0%{opacity:0;transform:translateY(-34px) scale(.55);}55%{opacity:1;transform:translateY(5px) scale(1.05);}78%{transform:translateY(-2px) scale(.99);}100%{opacity:1;transform:none;}}
@keyframes nova-spark{0%,100%{transform:rotate(0) scale(1);}50%{transform:rotate(8deg) scale(1.06);}}
@keyframes nova-pulse{0%,100%{opacity:.5;}50%{opacity:1;}}
@keyframes nova-bounce{0%,80%,100%{transform:translateY(0);opacity:.5;}40%{transform:translateY(-4px);opacity:1;}}
@keyframes nova-blink{0%,100%{opacity:1;}50%{opacity:0;}}
@media (prefers-reduced-motion:reduce){.nova-panel,.nova-canvas,.nova-in,.nova-launcher,.nova-avatar svg,.nova-dot,.nova-think,.nova-caret{animation:none!important;}}
`;
